import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  User, Calendar, CheckSquare, DollarSign, Bell, LogOut,
  Clock, TrendingUp, FileText, Menu, X as CloseIcon,
  Home, UserCircle, CalendarDays, Briefcase, Building2
} from 'lucide-react';
import { toast } from 'sonner';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

const EmployeeDashboard = () => {
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [employeeData, setEmployeeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('employeeToken');
    const employee = localStorage.getItem('employeeData');
    
    if (!token) {
      navigate('/employee-portal/login');
      return;
    }

    if (employee) {
      setEmployeeData(JSON.parse(employee));
    }

    fetchDashboard();
  }, [navigate]);

  const fetchDashboard = async () => {
    try {
      const token = localStorage.getItem('employeeToken');
      const response = await fetch(`${API_BASE_URL}/api/employee-portal/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      if (data.success) {
        setDashboardData(data);
      }
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('employeeToken');
    localStorage.removeItem('employeeData');
    toast.success('Logged out successfully');
    navigate('/employee-portal/login');
  };

  const menuItems = [
    { name: 'Dashboard', icon: Home, path: '/employee-portal/dashboard', active: true },
    { name: 'My Profile', icon: UserCircle, path: '/employee-portal/profile' },
    { name: 'Attendance', icon: Calendar, path: '/employee-portal/attendance' },
    { name: 'Leaves', icon: CalendarDays, path: '/employee-portal/leaves' },
    { name: 'Payroll', icon: DollarSign, path: '/employee-portal/payroll' },
    { name: 'Tasks', icon: Briefcase, path: '/employee-portal/tasks' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const employee = dashboardData?.employee || employeeData;

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <div className={`${sidebarOpen ? 'w-64' : 'w-20'} bg-white shadow-lg transition-all duration-300 fixed h-full z-10`}>
        <div className="p-4 border-b">
          <div className="flex items-center justify-between">
            {sidebarOpen && (
              <div className="flex items-center gap-2">
                <Building2 className="h-6 w-6 text-blue-600" />
                <span className="font-bold text-lg">Netix Portal</span>
              </div>
            )}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              {sidebarOpen ? <CloseIcon className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        <nav className="p-4 space-y-2">
          {menuItems.map((item) => (
            <button
              key={item.name}
              onClick={() => navigate(item.path)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                item.active ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <item.icon className="h-5 w-5" />
              {sidebarOpen && <span className="font-medium">{item.name}</span>}
            </button>
          ))}
        </nav>

        <div className="absolute bottom-4 left-0 right-0 px-4">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 text-red-600 hover:bg-red-50 rounded-lg"
          >
            <LogOut className="h-5 w-5" />
            {sidebarOpen && <span className="font-medium">Logout</span>}
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className={`flex-1 ${sidebarOpen ? 'ml-64' : 'ml-20'} transition-all duration-300`}>
        {/* Header */}
        <div className="bg-white shadow-sm">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Welcome back, {employee?.first_name}!
                </h1>
                <p className="text-sm text-gray-600">{employee?.job_title} • {employee?.department}</p>
              </div>
              <div className="flex items-center gap-4">
                <button className="relative p-2 text-gray-600 hover:bg-gray-100 rounded-lg">
                  <Bell className="h-5 w-5" />
                  {dashboardData?.notifications && dashboardData.notifications.length > 0 && (
                    <span className="absolute top-1 right-1 h-2 w-2 bg-red-500 rounded-full"></span>
                  )}
                </button>
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                    <span className="text-blue-600 font-semibold">
                      {employee?.first_name?.charAt(0)}{employee?.last_name?.charAt(0)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Dashboard Content */}
        <div className="p-6 space-y-6">
          {/* Quick Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Today's Status</p>
                    <p className="text-2xl font-bold text-green-600 mt-1">
                      {dashboardData?.today_attendance?.status || 'Not Marked'}
                    </p>
                  </div>
                  <div className="h-12 w-12 bg-green-100 rounded-lg flex items-center justify-center">
                    <Clock className="h-6 w-6 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Pending Tasks</p>
                    <p className="text-2xl font-bold text-blue-600 mt-1">
                      {dashboardData?.pending_tasks?.length || 0}
                    </p>
                  </div>
                  <div className="h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <CheckSquare className="h-6 w-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Leave Balance</p>
                    <p className="text-2xl font-bold text-orange-600 mt-1">
                      {dashboardData?.leave_balance?.casual?.balance || 0} Days
                    </p>
                  </div>
                  <div className="h-12 w-12 bg-orange-100 rounded-lg flex items-center justify-center">
                    <Calendar className="h-6 w-6 text-orange-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Notifications</p>
                    <p className="text-2xl font-bold text-purple-600 mt-1">
                      {dashboardData?.notifications?.filter(n => !n.is_read).length || 0}
                    </p>
                  </div>
                  <div className="h-12 w-12 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Bell className="h-6 w-6 text-purple-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Recent Activity & Tasks */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Today's Attendance */}
            <Card>
              <CardHeader>
                <CardTitle>Today's Attendance</CardTitle>
              </CardHeader>
              <CardContent>
                {dashboardData?.today_attendance ? (
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Status:</span>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        dashboardData.today_attendance.status === 'present'
                          ? 'bg-green-100 text-green-700'
                          : dashboardData.today_attendance.status === 'wfh'
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {dashboardData.today_attendance.status.toUpperCase()}
                      </span>
                    </div>
                    {dashboardData.today_attendance.check_in_time && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Check In:</span>
                        <span className="font-medium">
                          {new Date(dashboardData.today_attendance.check_in_time).toLocaleTimeString('en-IN', {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      </div>
                    )}
                    {dashboardData.today_attendance.work_hours && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Work Hours:</span>
                        <span className="font-medium">{dashboardData.today_attendance.work_hours} hrs</span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Clock className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No attendance marked for today</p>
                    <Button className="mt-4" size="sm">Mark Attendance</Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Pending Tasks */}
            <Card>
              <CardHeader>
                <CardTitle>Pending Tasks</CardTitle>
              </CardHeader>
              <CardContent>
                {dashboardData?.pending_tasks && dashboardData.pending_tasks.length > 0 ? (
                  <div className="space-y-3">
                    {dashboardData.pending_tasks.slice(0, 3).map((task, index) => (
                      <div key={index} className="p-3 bg-gray-50 rounded-lg">
                        <p className="font-medium text-sm">{task.title}</p>
                        <div className="flex justify-between items-center mt-2">
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            task.priority === 'high'
                              ? 'bg-red-100 text-red-700'
                              : task.priority === 'medium'
                              ? 'bg-orange-100 text-orange-700'
                              : 'bg-blue-100 text-blue-700'
                          }`}>
                            {task.priority}
                          </span>
                          <span className="text-xs text-gray-500">
                            Due: {new Date(task.due_date).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    ))}
                    <Button variant="outline" className="w-full mt-2" onClick={() => navigate('/employee-portal/tasks')}>
                      View All Tasks
                    </Button>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <CheckSquare className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No pending tasks</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Button
                  onClick={() => navigate('/employee-portal/attendance')}
                  variant="outline"
                  className="h-auto py-4 flex-col"
                >
                  <Calendar className="h-6 w-6 mb-2" />
                  <span className="text-sm">View Attendance</span>
                </Button>
                <Button
                  onClick={() => navigate('/employee-portal/leaves')}
                  variant="outline"
                  className="h-auto py-4 flex-col"
                >
                  <CalendarDays className="h-6 w-6 mb-2" />
                  <span className="text-sm">Apply Leave</span>
                </Button>
                <Button
                  onClick={() => navigate('/employee-portal/payroll')}
                  variant="outline"
                  className="h-auto py-4 flex-col"
                >
                  <DollarSign className="h-6 w-6 mb-2" />
                  <span className="text-sm">View Payslips</span>
                </Button>
                <Button
                  onClick={() => navigate('/employee-portal/profile')}
                  variant="outline"
                  className="h-auto py-4 flex-col"
                >
                  <UserCircle className="h-6 w-6 mb-2" />
                  <span className="text-sm">My Profile</span>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default EmployeeDashboard;
